import { useEffect, useState } from 'react';
import { InfoToggle } from '../components/InfoToggle';
import { Unavailable } from '../components/Unavailable';
import { publicEndpoint } from '../lib/endpoints';
import { SHOW_SUBGROVES } from '../lib/flags';

interface Props {
  apiUrl: string;
}

interface Indexer {
  indexer_did?: string;
  endpoint?: string;
  query_endpoint?: string;
  stake_amount?: number | string;
  status?: string;
  performance_score?: number;
  subgroves?: string[];
  last_update?: number;
  [k: string]: unknown;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; indexers: Indexer[] }
  | { kind: 'error' };

export function IndexersPanel({ apiUrl }: Props) {
  const [state, setState] = useState<State>({ kind: 'idle' });

  const load = async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetch(`${apiUrl}/indexers`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const raw = body?.data ?? body?.indexers ?? body;
      const indexers: Indexer[] = Array.isArray(raw) ? raw : [];
      setState({ kind: 'ok', indexers });
    } catch {
      setState({ kind: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const fmtStake = (n: number | string | undefined) => {
    if (n === undefined) return '—';
    const will = Math.round(Number(n) / 1e18);
    return `${will.toLocaleString()} WILL`;
  };

  // No endpoint a visitor could reach: no column, rather than a column of dashes.
  const showEndpoint =
    state.kind === 'ok' && state.indexers.some((idx) => publicEndpoint(idx.endpoint));

  return (
    <section className="panel">
      <h2>
        Indexers
        <InfoToggle>
          Lists registered indexers from <code>GET /indexers</code>.
          {SHOW_SUBGROVES && (
            <>
              {' '}
              Indexers run BlockchainIndexing subgrove pipelines and serve queries over{' '}
              <code>indexer.willow.tech</code>.
            </>
          )}
        </InfoToggle>
      </h2>

      {state.kind === 'loading' && <p className="subtle">Loading…</p>}

      {state.kind === 'error' && (
        <Unavailable onRetry={load} testid="indexers-error">
          Indexers aren't available right now.
        </Unavailable>
      )}

      {state.kind === 'ok' && (
        <>
          <p className="subtle">
            {state.indexers.length} indexer{state.indexers.length === 1 ? '' : 's'}
          </p>
          {state.indexers.length === 0 ? (
            <p className="subtle">No indexers registered.</p>
          ) : (
            <table className="subgrove-table">
              <thead>
                <tr>
                  <th>DID</th>
                  {showEndpoint && <th>Endpoint</th>}
                  <th>Stake</th>
                  <th>Score</th>
                  {SHOW_SUBGROVES && <th>Subgroves</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {state.indexers.map((idx, i) => (
                  <tr key={`${idx.indexer_did ?? i}`}>
                    <td>
                      <code>{idx.indexer_did ?? '—'}</code>
                    </td>
                    {showEndpoint && (
                      <td>
                        <code>{publicEndpoint(idx.endpoint) ?? '—'}</code>
                      </td>
                    )}
                    <td>{fmtStake(idx.stake_amount)}</td>
                    <td>{idx.performance_score?.toFixed(1) ?? '—'}</td>
                    {SHOW_SUBGROVES && <td>{idx.subgroves?.length ?? 0}</td>}
                    <td>
                      {idx.status === 'active' ? (
                        <span className="plan-badge plan-free">Active</span>
                      ) : (
                        <span className="muted">{idx.status ?? '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
